import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React, {FC, useMemo} from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {ModeType, useDialogModal, useDrawerModal, useGlobalActions} from "@/store/global.c";
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue, errDateMap, filterUsers, getRealId, getRealWrite, getSelectValues, setValues} from "@/helper/method";
import {useRoleUsers} from "@/store/user.c";
import {RoleUserVO} from "@/service/user.s";
import {isNumber} from "lodash";
import {TagOut} from "@/widget/TagOut/TagOut";
import {useCreateTask, useQueryTask, useUpdateTask} from "@/service/task.s";
import {AccessTimeOutlined} from '@mui/icons-material';
import {SearchData} from "@/widget/SearchData/SearchData";
import {SearchPlanVO} from "@/service/plan.s";
import {TaskPO} from "@/model/mission";

const validationSchema = z.object({
    id: z.unknown().default(0),
    taskName: z.string({message: "请填写任务名称"}),
    planId: z.number({message: "请选择关联目标"}),
    planName: z.ostring().optional(),
    status: z.ostring().default(""),
    statusId: z.number({message: "请选任务状态"}),
    executors: z.ostring(),
    executorIds: z.string({message: "请选择执行人"}),
    description: z.ostring(),
    planStageId: z.number({message: "请选择所属目标阶段"}),
    planStage: z.ostring().default(""),
    duration: z.number({message: "请选择预计工时"}),
    virtue: z.ostring().optional().default(""),
    virtueId: z.number({message: "请选择任务属性"}),
    priority: z.ostring().optional().default(""),
    parentId: z.unknown(),
    tempExecutors: z.array(z.object({username: z.string(), id: z.number()})).optional(),
    priorityId: z.number({message: "请选择优先级"}),
    startTime: z.coerce.date({
        errorMap: errDateMap("请选择开始时间")
    }),
    endTime: z.coerce.date({
        errorMap: errDateMap("请选择结束时间")
    }),
});

export type WriteTaskForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

interface WriteTaskProps {
    subtask?: boolean
    mode?: ModeType
}

export const WriteTask: FC<WriteTaskProps> = ({subtask = false, mode}) => {
    const allDict = useAllDict()
    const allUser = useRoleUsers([])
    const {taskStatus, planStage, taskPriority, taskVirtue} = allDict
    const {setToast} = useGlobalActions()
    const {mutateAsync: createTaskMutate, isPending: createTaskPending} = useCreateTask()
    const {mutateAsync: updateTaskMutate} = useUpdateTask()
    const {onSearch, id: drawerId, write: drawerWrite} = useDrawerModal()
    const {id: dialogId, write: dialogWrite} = useDialogModal()
    const id: number = getRealId(mode, drawerId, dialogId)
    const write: string = getRealWrite(mode, drawerWrite!, dialogWrite);
    const {setDrawerModal, setDialogModal} = useGlobalActions()
    const {data: taskData} = useQueryTask({id: id}, isNumber(id) && (!subtask || write == 'edit'))
    const task = taskData?.data || {} as TaskPO

    const {disabled, values} = useMemo(() => {
        if (subtask) {
            return {values: {parentId: task.id, planName: task.planName, planId: task.planId,}, disabled: true}
        } else if (write == 'edit') {
            return {disabled: false, values: {...task, tempExecutors: filterUsers(allUser, task?.executorIds)}}
        }
        return {disabled: false, values: {}}
    }, [subtask, write, task]);

    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WriteTaskForm>({
        resolver: zodResolver(validationSchema),
        values: values as WriteTaskForm,
        defaultValues,
    });

    const onSubmit = async (values: WriteTaskForm) => {
        const result = write == 'edit' ? await updateTaskMutate({...values, id: drawerId}) : await createTaskMutate(values)
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setDialogModal({open: false})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="taskName">任务名称</FormLabel>
                        <TextField
                            id="taskName"
                            {...register("taskName")}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 140},
                                },
                            }}
                            error={!!errors?.taskName}
                            helperText={errors?.taskName?.message}
                            placeholder="填写任务名称"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <SearchData<WriteTaskForm, SearchPlanVO> errMsg={errors?.planId?.message} readOnly={disabled} setValue={setValue} tag={5} attr={"planName"} attrId={"planId"}
                                                             label={"关联目标"}
                                                             control={control}/>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>任务状态</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={taskStatus}
                                value={value as any}
                                onChange={(_, option: DictPO | null) => {
                                    setValue("statusId", option!.value)
                                    onChange(option?.label)
                                }}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.statusId}
                                               helperText={errors?.statusId?.message}
                                               variant="outlined"
                                               placeholder={"选择任务状态"} {...params} />
                                )}
                            />
                        }} name={"status"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel htmlFor="tempExecutors">执行人</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                id="tempExecutors"
                                multiple={true}
                                options={allUser}
                                value={value}
                                disableListWrap={true}
                                onChange={(_, options) => {
                                    const values = getSelectValues(options, {key: "username", val: 'id'}, {key: "executorIds", val: "executors"})
                                    setValues(values, setValue as any)
                                    onChange(options)
                                }}
                                renderTags={(values) => {
                                    return values?.map((value: RoleUserVO, index: number) => <TagOut index={index} label={value.username}/>)
                                }}
                                isOptionEqualToValue={(option, value) => option.username === value.username}
                                disableCloseOnSelect={true}
                                getOptionLabel={(option: RoleUserVO) => option.username}
                                getOptionKey={(option: RoleUserVO) => option.username}
                                renderInput={(params) => (
                                    <TextField error={!!errors?.executorIds}
                                               sx={{"& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}}}
                                               helperText={errors?.executorIds?.message} placeholder={"选择执行人"} {...params} size={'small'}/>
                                )}
                            />
                        }} name={"tempExecutors"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="startTime">开始时间</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                id='account-settings-date'
                                locale='zhCN'
                                placeholderText={"选择开始时间"}
                                customInput={<TextField
                                    error={!!errors?.startTime}
                                    helperText={errors?.startTime?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={"startTime"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="endTime">结束时间</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                id='account-settings-date'
                                locale='zhCN'
                                placeholderText={"选择结束时间"}
                                customInput={<TextField
                                    error={!!errors?.endTime}
                                    helperText={errors?.endTime?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={"endTime"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>任务属性</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={taskVirtue}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("virtueId", option!.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.virtueId}
                                           helperText={errors?.virtueId?.message}
                                           variant="outlined"
                                           {...params}
                                           placeholder={"选择任务属性"}/>
                            )}
                        />} name={"virtue"}/>
                    </FormControl>
                </Grid>
                {/*<VirtueWatched control={control}/>*/}
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>优先级</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={taskPriority}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("priorityId", option!.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.priorityId}
                                           helperText={errors?.priorityId?.message}
                                           variant="outlined"
                                           placeholder={"选择优先级"} {...params} />
                            )}
                        />} name={"priority"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel htmlFor="planStage">所属目标阶段</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            id="planStage"
                            options={planStage}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("planStageId", option!.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.planStageId}
                                           helperText={errors?.planStageId?.message}
                                           variant="outlined"
                                           placeholder={"选择目标阶段"} {...params} />
                            )}
                        />} name={"planStage"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="duration">预计工时</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            error={!!errors?.duration}
                            helperText={errors?.duration?.message}
                            id="duration"
                            value={value}
                            onChange={event => onChange(parseInt(event.target.value) || '')}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 1000,
                                        maxLength: 3, inputMode: 'numeric', pattern: '[0-9]*'
                                    },
                                    endAdornment: <AccessTimeOutlined color="disabled"/>,
                                },
                            }}
                            placeholder="填写预计工时"
                            variant="outlined"
                        />} name={'duration'}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel htmlFor="description">任务描述</FormLabel>
                        <TextField
                            id="description"
                            multiline
                            rows={3}
                            {...register("description")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto', minHeight: 80},
                                },
                            }}
                            placeholder="任务描述"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createTaskPending} onReset={() => reset(defaultValues)}/>
        </Box>
    )
        ;
};
